import Ajv2020 from 'ajv/dist/2020';
import type { ValidateFunction } from 'ajv';
import { JSONSchema7 } from 'json-schema';
import { normalizeSchemaForFormHell } from '../../components/SchemaForm/schemaNormalization';
import { ErrorsByPointer, mapValidationErrors, SchemaValidationError } from './errorMapping';

export type SchemaValidationResult = {
    isValid: boolean;
    errors: SchemaValidationError[];
    errorsByPointer: ErrorsByPointer;
};

let ajvInstance: Ajv2020 | undefined;

/** Same dialect and options FormHell uses internally, so the save gate and the inline errors agree. */
const getAjv = (): Ajv2020 => {
    if (!ajvInstance) {
        ajvInstance = new Ajv2020({ allErrors: true, strict: false });
    }
    return ajvInstance;
};

type Compiled = { validate: ValidateFunction; normalized: JSONSchema7 };
const compiledCache = new WeakMap<object, Compiled>();

const compileSchema = (schema: JSONSchema7): Compiled => {
    const cached = compiledCache.get(schema);
    if (cached) {
        return cached;
    }
    const ajv = getAjv();
    const normalized = normalizeSchemaForFormHell(schema) as JSONSchema7;
    if (typeof normalized.$id === 'string' && ajv.getSchema(normalized.$id)) {
        ajv.removeSchema(normalized.$id);
    }
    const compiled = { validate: ajv.compile(normalized), normalized };
    compiledCache.set(schema, compiled);
    return compiled;
};

const buildResult = ({ errors, errorsByPointer }: ReturnType<typeof mapValidationErrors>): SchemaValidationResult => ({
    isValid: errors.length === 0,
    errors,
    errorsByPointer,
});

export const validateAgainstSchema = (data: unknown, schema: JSONSchema7): SchemaValidationResult => {
    let compiled: Compiled;
    try {
        compiled = compileSchema(schema);
    } catch (err: any) {
        return buildResult({
            errors: [{
                keyword: 'schema',
                instancePath: '',
                property: '',
                message: `schema is invalid: ${err?.message || 'unknown error'}`,
                params: {},
                schemaPath: '',
            }],
            errorsByPointer: { '': [`schema is invalid: ${err?.message || 'unknown error'}`] },
        });
    }
    compiled.validate(data);
    return buildResult(mapValidationErrors(compiled.validate.errors, compiled.normalized));
};
